import { TZDate } from '@date-fns/tz'
import { ComposerScheduleState, PostScheduleInfo } from 'shared'
import { BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_PINTEREST, BRAND_TIKTOK, BRAND_TWITTER, BRAND_YOUTUBE } from 'shared/constants'
import { PINTEREST_AI_MODIFIED, PINTEREST_AI_SYNTHETIC_PERFORMER, PinterestAiContentMode, PostDestinationType } from 'shared/types'
import { resolveTimezone } from 'utils/timezone'
import { ComposerVideoState, MediaItemState } from './state'

// Canonical preset comment-delay values (minutes). The UI label map (DELAY_OPTIONS)
// is keyed by these. lastCommentDelay only ever stores a preset: a preset delay is
// remembered as-is, a custom delay is remembered as the largest preset.
export const PRESET_COMMENT_DELAYS = [0, 1, 10] // eslint-disable-line no-magic-numbers

export const isPresetCommentDelay = (delay: number) => PRESET_COMMENT_DELAYS.includes(delay)

// Delay stored when a custom (non-preset) value is chosen — the largest preset (10).
export const DEFAULT_REMEMBERED_COMMENT_DELAY = PRESET_COMMENT_DELAYS[PRESET_COMMENT_DELAYS.length - 1]

export const toRememberedCommentDelay = (delay: number) =>
  isPresetCommentDelay(delay) ? delay : DEFAULT_REMEMBERED_COMMENT_DELAY

const BASE_TEN = 10

// The lowest repeat count that still means "repeat" — `getPostScheduleFromComposerState` drops the
// repeat entirely below it.
export const MIN_REPEAT_COUNT = 1

// EXPL: null when what was typed cannot yield a usable count — empty, mid-edit, or under the
// minimum. Callers decide what that means for them: the composer form keeps the last valid count in
// the store while the field is being retyped, the recycle selector falls back to the minimum.
export function parseRepeatCount(input: string): number | null {
  const parsed = parseInt(input, BASE_TEN)
  return Number.isNaN(parsed) || parsed < MIN_REPEAT_COUNT ? null : parsed
}

export function getPostScheduleFromComposerState(scheduleState: ComposerScheduleState, timezone: string): PostScheduleInfo {
  const date = new Date(scheduleState.date)
  const time = new Date(scheduleState.time)
  const year = date.getFullYear()
  const month = date.getMonth() // getMonth returns 0-11
  const day = date.getDate()
  const hour = time.getHours()
  const minute = time.getMinutes()

  // Create TZDate with year, month, day, hour, minute in the specified timezone.
  // EXPL: resolved here rather than at the call sites so the composer form and `createPost` — which
  // both build a schedule from the same state — always agree on the zone, even before the settings
  // defaults have landed and `timezone` is still ''.
  const tzDate = TZDate.tz(resolveTimezone(timezone), year, month, day, hour, minute)
  // Get the UTC timestamp and convert to ISO string
  const scheduledTime = new Date(tzDate.getTime()).toISOString()

  const schedule: PostScheduleInfo = {
    postDate: scheduledTime,
    repeat: false
  }

  if (scheduleState.repeat && scheduleState.repeatCount > 0) {
    const until = new Date(scheduleState.repeatUntilDate)
    // EXPL: repeat_until must have time set to 23:59
    until.setHours(23, 59, 0) // eslint-disable-line no-magic-numbers
    schedule.repeat = true
    schedule.repeatLength = scheduleState.repeatCount
    schedule.repeatPeriod = scheduleState.repeatPeriod
    schedule.repeatUntil = scheduleState.repeatUntil === 'forever'
      ? undefined
      : until.toISOString()
  }
  return schedule
}

export function randomId() {
  const uint32 = window.crypto.getRandomValues(new Uint32Array(1))[0]
  // eslint-disable-next-line no-magic-numbers
  return uint32.toString(16)
}

export function mergeFilesState(
  images: Record<string, MediaItemState>,
  video: ComposerVideoState | undefined,
  contextFiles: Record<string, MediaItemState>
): Record<string, MediaItemState> {
  // EXPL: the media context only holds the local File and its upload handle; every piece of metadata,
  // `url` above all, belongs to the store. The context entry is seeded from the payload it was added
  // with, so it can carry a url the store has since replaced — hence the store spread goes last.
  const imagesData = Object.keys(images).reduce((acc, key) => {
    const imageFromContext = contextFiles[key]
    if (imageFromContext) {
      acc[key] = {
        ...imageFromContext,
        ...images[key]
      }
    } else {
      acc[key] = images[key]
    }
    return acc
  }, {} as Record<string, any>)

  const videoData = video ? {
    [video.id]: {
      ...(contextFiles[video.id] || {}),
      ...video,
      isVideo: true
    }
  } : {}

  return Object.assign({}, imagesData, videoData)
}

// EXPL: the networks whose posts carry the `isAiContent` disclosure - one checkbox drives all of them, in the composer
// and on each bulk row
export const AI_CONTENT_NETWORKS: PostDestinationType[] = [
  BRAND_FACEBOOK, BRAND_INSTAGRAM, BRAND_PINTEREST, BRAND_TWITTER, BRAND_YOUTUBE, BRAND_TIKTOK
]

/**
 * Pinterest takes a single AI mode: the pin's "AI-generated person" pick wins, otherwise the AI content flag means
 * "AI modified". `null` (not `undefined`) so an edit clears a mode the post already had.
 */
export function toPinterestAiContentMode(isAiContent: boolean, mode?: PinterestAiContentMode): PinterestAiContentMode | null {
  if (mode === PINTEREST_AI_SYNTHETIC_PERFORMER) {
    return PINTEREST_AI_SYNTHETIC_PERFORMER
  }
  return isAiContent ? PINTEREST_AI_MODIFIED : null
}
